import { useEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import api, { rows, pageMeta } from "@/lib/api";

/**
 * Lista com scroll infinito: carrega a próxima página sozinha quando o elemento
 * apontado por `sentinelRef` chega perto da viewport.
 *
 * Uso: coloque <div ref={sentinelRef} /> logo abaixo do grid.
 *
 *   const { items, total, sentinelRef, isLoading, isFetchingNextPage, hasNextPage } =
 *     useInfiniteList("/products", { category, search });
 */
export default function useInfiniteList(path, params = {}, { perPage = 24, enabled = true } = {}) {
  const sentinelRef = useRef(null);

  const query = useInfiniteQuery({
    queryKey: [path, params, perPage],
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await api.get(path, { params: { ...params, page: pageParam, per_page: perPage } });
      return { items: rows(res), meta: pageMeta(res) };
    },
    // Endpoint ainda não paginado (meta null) → uma página só, sem loop infinito.
    getNextPageParam: (last, all) => (last.meta?.has_more ? all.length + 1 : undefined),
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !isFetchingNextPage) fetchNextPage(); },
      { rootMargin: "300px" }, // antecipa o carregamento antes de bater no fim
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  return {
    ...query,
    items,
    total: query.data?.pages[0]?.meta?.total ?? items.length,
    sentinelRef,
  };
}
